import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslations } from '@/i18n';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { cn } from '@/lib/utils';

interface SavedExpenseCardProps {
  icon: string;
  title: string;
  subtitle?: string;
  dateRange?: {
    checkInDate: Date;
    checkOutDate: Date;
  };
  amount: string;
  currency?: string;
  formattedAmount?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  showActions?: boolean;
  isDeleting?: boolean;
  /** Whether dates have validation issues (outside allowed range) */
  hasDateIssue?: boolean;
}

export default function SavedExpenseCard({
  icon,
  title,
  subtitle,
  dateRange,
  amount,
  currency,
  formattedAmount,
  onEdit,
  onDelete,
  showActions = true,
  isDeleting = false,
  hasDateIssue = false,
}: SavedExpenseCardProps) {
  const tTrips = useTranslations('trips');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const hasSubtitle = Boolean(subtitle);
  const hasDateRange = Boolean(dateRange);
  const formattedDateRange =
    dateRange != null
      ? `${format(dateRange.checkInDate, 'dd/MM/yyyy')} - ${format(
          dateRange.checkOutDate,
          'dd/MM/yyyy'
        )}`
      : null;

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowDeleteConfirm(false);
  };

  const renderActionButtons = (wrapperClassName?: string) => {
    if (!showActions) {
      return null;
    }

    const canRenderEdit = Boolean(onEdit);
    const canRenderDelete = Boolean(onDelete);

    if (!canRenderEdit && !canRenderDelete) {
      return null;
    }

    return (
      <div className={cn('flex items-center gap-2', wrapperClassName)}>
        {canRenderEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 transition-colors hover:bg-[#f9f9f9] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={tTrips('actions.edit')}
          >
            <img src="/assets/icons/edit.svg" alt="" className="h-5 w-5" />
          </button>
        )}

        {canRenderDelete && (
          <button
            type="button"
            onClick={handleDeleteClick}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 transition-colors hover:bg-[#f9f9f9] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={tTrips('actions.delete')}
            disabled={isDeleting}
          >
            <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={cn(
          'hidden w-full rounded-2xl border p-5 lg:block',
          hasDateIssue ? 'border-[#e5484d] bg-[#fff5f5]' : 'border-[#ebebeb] bg-[#f9f9f9]'
        )}
      >
        <div className="flex flex-col gap-5">
          {/* Header with icon and action buttons */}
          <div className="flex items-center justify-between">
            <div className="p-1">
              <div className="flex shrink-0 items-center justify-center rounded-[2rem] border border-[#f1f1f1] bg-white p-2">
                <img src={icon} alt="" className="h-6 w-6" />
              </div>
            </div>

            {renderActionButtons()}
          </div>

          {/* Content */}
          <div className="flex flex-col gap-2">
            {/* Title - full width */}
            <h3 className="text-2xl leading-8 font-medium text-[#292929]">{title}</h3>
            {hasSubtitle && (
              <span className="text-[15px] leading-6 break-words text-[#666666]">{subtitle}</span>
            )}

            {/* Date and Amount row */}
            <div className="flex items-end justify-between gap-5">
              {/* Left side - Date */}
              <div className="flex min-w-0 flex-1 flex-col items-start">
                {hasDateRange && formattedDateRange && (
                  <div className="flex items-center gap-2">
                    <img
                      src="/assets/icons/calendar.svg"
                      alt=""
                      className="h-[18px] w-[18px] shrink-0"
                    />
                    <span
                      className={cn(
                        'text-[15px] leading-6',
                        hasDateIssue ? 'text-[#e5484d]' : 'text-[#666666]'
                      )}
                    >
                      {formattedDateRange}
                    </span>
                  </div>
                )}
              </div>

              {/* Right side - Amount */}
              <div className="flex shrink-0 items-end">
                <div className="min-w-[124px] text-right text-2xl leading-8 font-medium text-[#292929]">
                  {formattedAmount ?? (currency ? `${currency} ${amount}` : amount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'w-full rounded-2xl border px-4 py-5 lg:hidden',
          hasDateIssue ? 'border-[#e5484d] bg-[#fff5f5]' : 'border-[#ebebeb] bg-[#f9f9f9]'
        )}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
            <img src={icon} alt="" className="h-6 w-6" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base leading-6 font-medium text-[#292929]">{title}</h3>
                {hasSubtitle && (
                  <span className="mt-1 block text-sm leading-5 break-words text-[#666666]">
                    {subtitle}
                  </span>
                )}
                {hasDateRange && formattedDateRange && (
                  <div
                    className={cn(
                      'mt-2 flex items-center gap-2 text-sm leading-5',
                      hasDateIssue ? 'text-[#e5484d]' : 'text-[#666666]'
                    )}
                  >
                    <img
                      src="/assets/icons/calendar.svg"
                      alt=""
                      className="h-4 w-4 shrink-0"
                    />
                    <span className="break-words">{formattedDateRange}</span>
                  </div>
                )}
              </div>
              {renderActionButtons('flex-shrink-0')}
            </div>
            <div className="flex justify-between gap-3">
              <div className="flex flex-col text-sm leading-5 text-[#666666]">
                {hasDateRange ? (
                  <span className="break-words text-[#292929]">
                    {formattedAmount ?? (currency ? `${currency} ${amount}` : amount)}
                  </span>
                ) : null}
                {!hasDateRange && (
                  <span className="text-base leading-6 font-medium text-[#292929]">
                    {formattedAmount ?? (currency ? `${currency} ${amount}` : amount)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title={tTrips('deleteExpenseConfirmation.title')}
        message={tTrips('deleteExpenseConfirmation.message')}
        confirmText={tTrips('deleteExpenseConfirmation.confirm')}
        cancelText={tTrips('deleteExpenseConfirmation.cancel')}
        confirmButtonVariant="danger"
        isLoading={isDeleting}
      />
    </>
  );
}
